/**
 * GFM footnotes (decisions 35, 36; `@lezer/markdown` ships none): references `[^label]` anywhere inline, and
 * definitions `[^label]: …` that start a block. A label is one to 999 characters with no whitespace and no
 * unescaped `[` or `]`. Whether a reference has a definition is decided after parsing (footnote-index.ts),
 * since a definition may come after its references.
 *
 * A definition is a container, as in GFM: the rest of its first line and every following line indented four
 * columns past the definition's own container belong to it (blank lines too), so a note can hold several
 * paragraphs, a quote or a list; a paragraph's unindented next line continues it lazily, as in a list item.
 * Inside, the content column is the base, so part 02's rules (plain paragraphs, depth limit) apply there as
 * everywhere. As in GFM, a definition line interrupts a paragraph (definitions on consecutive lines are
 * separate), a definition is never a link reference definition (`[^1]: word`), and `![^1]` is a `!` and a
 * reference, not an image.
 */
import type { BlockContext, Element, InlineContext, Line, MarkdownConfig } from '@lezer/markdown';
import { BACKSLASH, CARET, isSpace } from './chars.ts';

const EXCLAMATION = 33;
const OPEN_BRACKET = 91;
const CLOSE_BRACKET = 93;
const COLON = 58;
/** CommonMark's longest link label, which GFM applies to footnote labels. */
const MAX_LABEL = 999;

/**
 * Where the `]` closing a footnote label ends, for `text` holding `[^` at `start`; -1 when there is no valid
 * label. Reading past the end gives NaN, which matches nothing and fails the scan.
 */
export function footnoteLabelEnd(text: string, start: number): number {
  if (text.charCodeAt(start) !== OPEN_BRACKET || text.charCodeAt(start + 1) !== CARET) return -1;
  const first = start + 2;
  for (let pos = first; pos - first <= MAX_LABEL; pos += 1) {
    const code = text.charCodeAt(pos);
    if (code === CLOSE_BRACKET) return pos > first ? pos : -1;
    if (Number.isNaN(code) || code === OPEN_BRACKET || isSpace(code)) return -1;
    if (code === BACKSLASH) {
      const escaped = text.charCodeAt(pos + 1);
      if (Number.isNaN(escaped) || isSpace(escaped)) return -1;
      pos += 1;
    }
  }
  return -1;
}

/** Where a definition's label closes in `text` from `start`, or -1 when no definition starts there. */
function definitionClose(text: string, start: number): number {
  const close = footnoteLabelEnd(text, start);
  // A failed label is -1, which this returns whatever follows.
  return text.charCodeAt(close + 1) === COLON ? close : -1;
}

/** A reference's or definition's opening marks and label, as elements. */
function labelParts(cx: BlockContext | InlineContext, from: number, close: number, closeLength: number): Element[] {
  return [cx.elt('FootnoteMark', from, from + 2), cx.elt('FootnoteLabel', from + 2, close), cx.elt('FootnoteMark', close, close + closeLength)];
}

/** How far past its container a definition's continuation lines are indented (GFM). */
const CONTENT_INDENT = 4;

/** The extension. */
export const footnotes: MarkdownConfig = {
  defineNodes: [
    {
      name: 'FootnoteDefinition',
      block: true,
      composite(_cx: BlockContext, line: Line, indent: number): boolean {
        if (line.next !== -1 && line.indent < line.baseIndent + indent) return false;
        line.moveBaseColumn(line.baseIndent + indent);
        return true;
      },
    },
    'FootnoteReference',
    'FootnoteLabel',
    'FootnoteMark',
  ],
  parseBlock: [
    {
      name: 'FootnoteDefinition',
      before: 'Blockquote',
      parse(cx: BlockContext, line: Line): null | false {
        // `line.next` is what plain paragraphs hide (plain-paragraph.ts): nothing opens where it is hidden.
        if (line.next !== OPEN_BRACKET) return false;
        const close = definitionClose(line.text, line.pos);
        if (close === -1) return false;
        const from = cx.lineStart + line.pos;
        cx.startComposite('FootnoteDefinition', line.pos, CONTENT_INDENT);
        for (const part of labelParts(cx, from, cx.lineStart + close, 2)) cx.addElement(part);
        line.moveBase(close + 2);
        return null;
      },
      endLeaf(_cx: BlockContext, line: Line): boolean {
        return definitionClose(line.text, line.pos) !== -1;
      },
    },
  ],
  parseInline: [
    {
      name: 'FootnoteReference',
      before: 'Link',
      parse(cx: InlineContext, next: number, pos: number): number {
        // Lezer offers every position of the inline content: only a `[` or a `!` can start one.
        if (next !== OPEN_BRACKET && next !== EXCLAMATION) return -1;
        // As in GFM, `![^1]` is a `!` and a reference, not an image: the `!` is passed over as text.
        if (next === EXCLAMATION) return footnoteLabelEnd(cx.text, pos + 1 - cx.offset) === -1 ? -1 : pos + 1;
        const close = footnoteLabelEnd(cx.text, pos - cx.offset);
        if (close === -1) return -1;
        const end = cx.offset + close;
        return cx.addElement(cx.elt('FootnoteReference', pos, end + 1, labelParts(cx, pos, end, 1)));
      },
    },
  ],
};
